<section class="consolidated-report">

    <!-- Filter Card -->
    <div class="card m-b-20">
        <div class="card-header">
            <i class="fa fa-bar-chart label-icon" aria-hidden="true"></i>
            <label class="p-l-5 m-b-0">Profit Center Consolidated Report</label>
        </div>
        <div class="card-body">

            <!-- No store selected -->
            <div *ngIf="!storeID" class="alert alert-info m-b-0">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                Please select a <strong>Store</strong> from the navigation tree to generate a report.
            </div>

            <div *ngIf="storeID">

                <!-- Main filter row -->
                <div class="row align-items-end">

                    <!-- Store -->
                    <div class="col-sm-2">
                        <label class="text-muted filter-label">Store</label>
                        <p class="filter-value">
                            <i class="fa fa-map-marker text-info m-r-5" aria-hidden="true"></i>
                            <strong>{{ storeName || storeID }}</strong>
                        </p>
                    </div>

                    <!-- Profit Center -->
                    <div class="col-sm-2">
                        <label class="text-muted filter-label">Profit Center</label>
                        <ng-select
                            [(ngModel)]="selectedPCtrID"
                            [items]="profitCentersWithAll"
                            bindLabel="xName"
                            bindValue="_id"
                            (ngModelChange)="isGenerated = false"
                        ></ng-select>
                    </div>

                    <!-- Report Type -->
                    <div class="col-sm-2">
                        <label class="text-muted filter-label">Report Type</label>
                        <div class="d-flex align-items-center report-type-row">
                            <label class="radio-inline m-r-15 m-b-0">
                                <input type="radio" [(ngModel)]="reportType" value="summary" (ngModelChange)="isGenerated = false" />
                                <span class="m-l-5">Summary</span>
                            </label>
                            <label class="radio-inline m-b-0">
                                <input type="radio" [(ngModel)]="reportType" value="detailed" (ngModelChange)="isGenerated = false" />
                                <span class="m-l-5">Detailed</span>
                            </label>
                        </div>
                    </div>

                    <!-- Start Date -->
                    <div class="col-sm-2">
                        <label class="text-muted filter-label">Start Date</label>
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            bsDatepicker
                            [(ngModel)]="startDate"
                            [bsConfig]="bsConfig"
                            placeholder="MM/DD/YYYY"
                        />
                    </div>

                    <!-- End Date -->
                    <div class="col-sm-2">
                        <label class="text-muted filter-label">End Date</label>
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            bsDatepicker
                            [(ngModel)]="endDate"
                            [bsConfig]="bsConfig"
                            placeholder="MM/DD/YYYY"
                        />
                    </div>

                    <!-- Generate button -->
                    <div class="col-sm-2">
                        <button
                            class="btn btn-info btn-sm"
                            type="button"
                            (click)="generateReport()"
                            [disabled]="isLoading || !selectedPCtrID || dateRangeInvalid"
                        >
                            <i
                                class="fa"
                                [ngClass]="isLoading ? 'fa-spinner fa-spin' : 'fa-bar-chart'"
                                aria-hidden="true"
                            ></i>
                            {{ isLoading ? 'Generating...' : 'Generate Report' }}
                        </button>
                    </div>

                </div>

                <!-- Date range warning -->
                <div *ngIf="dateRangeInvalid" class="alert alert-warning m-t-10 m-b-0">
                    <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                    Date range cannot exceed 31 days.
                </div>

                <!-- Error -->
                <div *ngIf="errorMessage" class="alert alert-danger m-t-15 m-b-0">
                    <i class="fa fa-exclamation-triangle" aria-hidden="true"></i> {{ errorMessage }}
                </div>

            </div>
        </div>
    </div>

    <!-- Loading -->
    <div *ngIf="isLoading" class="text-center m-t-30 m-b-30">
        <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
        <p class="text-muted m-t-10">Generating report...</p>
        <p *ngIf="loadingProgress" class="text-muted m-t-5">{{ loadingProgress }}</p>
    </div>

    <!-- ── RESULTS WRAPPER (PDF target) ──────────────────────────── -->
    <div #reportResults>

    <!-- ── SUMMARY RESULTS ─────────────────────────────────────────── -->
    <div *ngIf="isGenerated && reportType === 'summary' && !isLoading">

        <div *ngIf="summaryRows.length === 0" class="card">
            <div class="card-body text-center p-30">
                <i class="fa fa-bar-chart fa-2x text-muted"></i>
                <h5 class="text-muted m-t-10">No data found</h5>
                <p class="text-muted m-b-0">No transactions found for the selected period.</p>
            </div>
        </div>

        <div *ngIf="summaryRows.length > 0" class="card">
            <div class="card-header">
                <i class="fa fa-table label-icon" aria-hidden="true"></i>
                <label class="p-l-5 m-b-0">
                    Summary — {{ storeName }}
                    <small *ngIf="startDate || endDate" class="text-muted">
                        &nbsp;({{ startDate | date:'MM/dd/yyyy' }} – {{ endDate | date:'MM/dd/yyyy' }})
                    </small>
                </label>
            </div>
            <div class="card-body p-0">
                <table class="table table-bordered table-hover report-table m-b-0">
                    <thead class="thead-light">
                        <tr>
                            <th>Profit Center</th>
                            <th class="text-right">Total Revenue</th>
                            <th class="text-right">Total Expenses</th>
                            <th class="text-right">Net Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let row of summaryRows">
                            <td>{{ row.name }}</td>
                            <td class="text-right">{{ row.revenue | currency:'USD' }}</td>
                            <td class="text-right">{{ row.expenses | currency:'USD' }}</td>
                            <td class="text-right">{{ row.net | currency:'USD' }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr class="grand-total-row">
                            <td><strong>TOTAL</strong></td>
                            <td class="text-right"><strong>{{ getSummaryGrandTotal().revenue | currency:'USD' }}</strong></td>
                            <td class="text-right"><strong>{{ getSummaryGrandTotal().expenses | currency:'USD' }}</strong></td>
                            <td class="text-right"><strong>{{ getSummaryGrandTotal().net | currency:'USD' }}</strong></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>

    </div>

    <!-- ── DETAILED RESULTS ────────────────────────────────────────── -->
    <div *ngIf="isGenerated && reportType === 'detailed' && !isLoading">

        <div *ngIf="detailedGroups.length === 0" class="card">
            <div class="card-body text-center p-30">
                <i class="fa fa-list fa-2x text-muted"></i>
                <h5 class="text-muted m-t-10">No transactions found</h5>
                <p class="text-muted m-b-0">No settled transactions found for the selected period.</p>
            </div>
        </div>

        <div *ngIf="detailedGroups.length > 0">

            <!-- Report header -->
            <div class="card m-b-10">
                <div class="card-header">
                    <i class="fa fa-list label-icon" aria-hidden="true"></i>
                    <label class="p-l-5 m-b-0">
                        Detailed — {{ storeName }}
                        <small *ngIf="startDate || endDate" class="text-muted">
                            &nbsp;({{ startDate | date:'MM/dd/yyyy' }} – {{ endDate | date:'MM/dd/yyyy' }})
                        </small>
                    </label>
                </div>
            </div>

            <!-- One block per profit center -->
            <div *ngFor="let group of detailedGroups" class="card m-b-20">
                <div class="card-header pctr-group-header">
                    <i class="fa fa-building-o label-icon" aria-hidden="true"></i>
                    <label class="p-l-5 m-b-0">{{ group.pctrName }}</label>
                    <span class="badge badge-info pull-right">{{ group.txns.length }} txn(s)</span>
                </div>
                <div class="card-body p-0">
                    <div class="table-responsive">
                        <table class="table table-bordered table-hover table-sm detail-table m-b-0">
                            <thead class="thead-light">
                                <tr>
                                    <th>Type</th>
                                    <th>Invoice</th>
                                    <th>Customer #</th>
                                    <th>Card</th>
                                    <th>Account #</th>
                                    <th class="text-right">Amt</th>
                                    <th class="text-right">SurChg</th>
                                    <th class="text-right">Tip</th>
                                    <th class="text-right">TtlAmt</th>
                                    <th class="text-right">MerAmt</th>
                                    <th>Auth Date</th>
                                    <th>Batch Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let t of group.txns">
                                    <td>{{ t.xTxnActTag || '-' }}</td>
                                    <td>{{ t.xInvNum || '-' }}</td>
                                    <td>{{ t.xCustNum || '-' }}</td>
                                    <td>{{ t.xBrandTag || '-' }}</td>
                                    <td>{{ t.xMaskedAcctNum || '-' }}</td>
                                    <td class="text-right">{{ t.xMainAmt }}</td>
                                    <td class="text-right">{{ t.xIncSurcAmt }}</td>
                                    <td class="text-right">{{ t.xTipAmt }}</td>
                                    <td class="text-right">{{ t.xTtlAmt }}</td>
                                    <td class="text-right">{{ t.xMerAmt }}</td>
                                    <td>{{ t.xAuthDateTime }}</td>
                                    <td>{{ t.xSettleDateTime }}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr class="subtotal-row">
                                    <td colspan="5"><strong>Subtotal</strong></td>
                                    <td class="text-right"><strong>{{ group.subtotal.xMainAmt | currency:'USD' }}</strong></td>
                                    <td class="text-right"><strong>{{ group.subtotal.xIncSurcAmt | currency:'USD' }}</strong></td>
                                    <td class="text-right"><strong>{{ group.subtotal.xTipAmt | currency:'USD' }}</strong></td>
                                    <td class="text-right"><strong>{{ group.subtotal.xTtlAmt | currency:'USD' }}</strong></td>
                                    <td class="text-right"><strong>{{ group.subtotal.xMerAmt | currency:'USD' }}</strong></td>
                                    <td colspan="2"></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Grand Total -->
            <div class="card grand-total-card">
                <div class="card-body p-0">
                    <table class="table table-bordered m-b-0 grand-total-table">
                        <tbody>
                            <tr class="grand-total-row">
                                <td style="width:42%"><strong>GRAND TOTAL</strong></td>
                                <td class="text-right"><strong>{{ getDetailedGrandTotal().xMainAmt | currency:'USD' }}</strong></td>
                                <td class="text-right"><strong>{{ getDetailedGrandTotal().xIncSurcAmt | currency:'USD' }}</strong></td>
                                <td class="text-right"><strong>{{ getDetailedGrandTotal().xTipAmt | currency:'USD' }}</strong></td>
                                <td class="text-right"><strong>{{ getDetailedGrandTotal().xTtlAmt | currency:'USD' }}</strong></td>
                                <td class="text-right"><strong>{{ getDetailedGrandTotal().xMerAmt | currency:'USD' }}</strong></td>
                                <td style="width:16%"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    </div>

    </div><!-- /#reportResults -->

    <!-- ── EXPORT BUTTONS ──────────────────────────────────────────── -->
    <div *ngIf="isGenerated && !isLoading" class="card export-bar">
        <div class="card-body d-flex justify-content-end align-items-center p-10">
            <span class="text-muted m-r-15" style="font-size:13px;">Export report as:</span>
            <button
                class="btn btn-default btn-sm m-r-10"
                type="button"
                (click)="exportPdf()"
                [disabled]="isPdfLoading"
            >
                <i class="fa" [ngClass]="isPdfLoading ? 'fa-spinner fa-spin' : 'fa-file-pdf-o'" aria-hidden="true"></i>
                {{ isPdfLoading ? 'Preparing...' : 'PDF' }}
            </button>
            <button
                class="btn btn-success btn-sm"
                type="button"
                (click)="exportCsv()"
                [disabled]="isCsvLoading"
            >
                <i class="fa" [ngClass]="isCsvLoading ? 'fa-spinner fa-spin' : 'fa-file-excel-o'" aria-hidden="true"></i>
                {{ isCsvLoading ? 'Preparing...' : 'CSV' }}
            </button>
        </div>
    </div>

</section>
